'use client'

import { Icon } from '@dc-shared-web/components/base'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  Textarea,
} from '@dc-shared-web/components/shadcn'
import { useFSTrackEvent } from '@dc-shared-web/hooks'
import { cn } from '@dc-shared-web/lib'
import {
  faArrowsFromLine,
  faArrowsToLine,
  faCircleQuestion,
  faClock,
  faFileMagnifyingGlass,
  faXmark,
  IconDefinition,
} from '@document-crunch/pro-regular-svg-icons'
import { useEffect, useRef, useState } from 'react'

type FeedbackOption = {
  text: string
  icon: IconDefinition
}

export type Feedback = {
  options: string[]
  other?: string
  improvement?: string
}

const OTHER_OPTION = 'Other'

const defaultOptions: FeedbackOption[] = [
  { text: 'Inaccurate', icon: faXmark },
  { text: 'Incomplete', icon: faXmark },
  { text: 'Hard to understand', icon: faCircleQuestion },
  { text: 'Missing references', icon: faFileMagnifyingGlass },
  { text: 'Too long', icon: faArrowsFromLine },
  { text: 'Too short', icon: faArrowsToLine },
  { text: 'Result took too long', icon: faClock },
  { text: OTHER_OPTION, icon: faCircleQuestion },
]

const shouldClearError = (opts: string[], text: string) =>
  opts.length > 0 && text.trim() !== ''

export function NegativeFeedbackDialog({
  open,
  setOpen,
  options = defaultOptions,
  onSubmit,
  className,
  feedbackContext: {
    pageCategory = 'Crunch View',
    subpageCategory,
    entityId,
    entityType = 'crunch',
  },
}: {
  open: boolean
  setOpen: (open: boolean) => void
  options?: FeedbackOption[]
  onSubmit: (feedback: Feedback) => void
  className?: string
  feedbackContext: {
    pageCategory?: string
    subpageCategory?: string
    entityId?: string
    entityType?: string
  }
}) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [otherText, setOtherText] = useState('')
  const [improvementText, setImprovementText] = useState('')
  const [showOther, setShowOther] = useState(false)
  const [showOtherRequired, setShowOtherRequired] = useState(false)
  const [showValidationError, setShowValidationError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submittingRef = useRef(false)

  const { trackEvent } = useFSTrackEvent()

  const handleOptionClick = (option: string) => {
    let newSelectedOptions: string[]
    if (selectedOptions.includes(option)) {
      newSelectedOptions = selectedOptions.filter((o) => o !== option)
      setSelectedOptions(newSelectedOptions)
    } else {
      newSelectedOptions = [...selectedOptions, option]
      setSelectedOptions(newSelectedOptions)
      if (
        showValidationError &&
        shouldClearError(newSelectedOptions, improvementText)
      ) {
        setShowValidationError(false)
      }
    }
  }

  const handleSubmit = () => {
    if (submittingRef.current) return

    if (showOther && otherText.trim() === '') {
      return setShowOtherRequired(true)
    }

    const hasExperienceSelection = selectedOptions.length > 0
    const hasImprovementText = improvementText.trim() !== ''

    if (!hasExperienceSelection || !hasImprovementText) {
      return setShowValidationError(true)
    }

    submittingRef.current = true
    setIsSubmitting(true)

    trackEvent('AIFeedback', {
      name: `${pageCategory} - ${subpageCategory}`,
      title: document.title,
      path: window.location.pathname,
      page_category: pageCategory,
      subpage_category: subpageCategory,
      entity_id: entityId,
      entity_type: entityType,
      feedback_type: 'negative',
      feedback_reasons: selectedOptions,
      feedback_other: otherText,
      feedback_improvement: improvementText,
    })
    onSubmit({
      options: selectedOptions,
      other: otherText,
      improvement: improvementText,
    })
  }

  const handleOtherTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOtherText(e.target.value)
    setShowOtherRequired(e.target.value.trim() === '')
  }

  useEffect(() => {
    if (selectedOptions.includes(OTHER_OPTION)) {
      setShowOther(true)
    } else {
      setShowOther(false)
    }
  }, [selectedOptions])

  useEffect(() => {
    if (!open) {
      setShowOtherRequired(false)
      setShowOther(false)
      setOtherText('')
      setImprovementText('')
      setSelectedOptions([])
      setShowValidationError(false)
      setIsSubmitting(false)
      submittingRef.current = false
    }
  }, [open])

  useEffect(() => {
    if (!showOther) {
      setOtherText('')
      setShowOtherRequired(false)
    }
  }, [showOther])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        overlayClassname='tw-z-[100]'
        className={cn(
          'tw-max-w-lg tw-w-[calc(100vw-1rem)] tw-h-[calc(100vh-1rem)] sm:tw-h-auto sm:tw-w-auto tw-overflow-hidden',
          className
        )}
        data-testid='negative-feedback-modal-content'
      >
        <div className='tw-flex tw-content-between'>
          <h1 className='tw-font-medium tw-truncate tw-w-full tw-text-neutral-700 tw-text-xl'>
            Help us improve
          </h1>
          <Button
            className='tw-p-0 tw--mt-2 tw--mr-2 focus-visible:tw-ring-2 focus-visible:tw-ring-primary-600'
            data-testid='feedback-modal-close-btn'
            variant='ghost'
            size={'icon'}
            onClick={() => setOpen(false)}
          >
            <Icon icon={faXmark} size='xl' className='tw-text-neutral-400' />
          </Button>
        </div>{' '}
        <DialogDescription className='tw-text-neutral-500 tw-text-base'>
          Your feedback helps us improve the experience for everyone.
          <p className='tw-text-2xs tw-font-normal tw-text-neutral-400'>
            * All questions are required
          </p>
        </DialogDescription>
        <div className='tw-h-[calc(100%-120px)] tw-p-1 tw-overflow-y-auto tw-flex tw-flex-col tw-gap-4'>
          <div className='tw-flex tw-flex-col tw-gap-2'>
            <p className='tw-text-xs tw-font-medium tw-text-neutral-700'>
              What did you experience? Select all that apply.
            </p>
            <div
              className={cn(
                'tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2 tw-rounded tw-p-2 tw-border tw-border-transparent',
                showValidationError &&
                  selectedOptions.length === 0 &&
                  'tw-border-destructive-600'
              )}
            >
              {options.map((option, i) => (
                <Button
                  variant='outline'
                  className={cn(
                    'tw-flex tw-px-2 md:tw-px-3 tw-justify-start tw-text-sm tw-gap-2 focus-visible:tw-ring-2 focus-visible:tw-ring-primary-600 tw-text-primary-600 hover:tw-text-primary-600 hover:tw-bg-neutral-50',
                    selectedOptions.includes(option.text) &&
                      'tw-bg-neutral-100 hover:tw-bg-neutral-100'
                  )}
                  key={option.text}
                  data-testid={`feedback-modal-option-${i}`}
                  aria-pressed={selectedOptions.includes(option.text)}
                  onClick={() => handleOptionClick(option.text)}
                >
                  <Icon
                    size='sm'
                    className='tw-text-primary-600 tw-w-4'
                    icon={option.icon}
                  />
                  <span className='tw-text-left tw-text-sm  sm:tw-text-2xs md:tw-text-sm'>
                    {option.text}
                  </span>
                </Button>
              ))}
            </div>
          </div>
          {showOther && (
            <div className='tw-flex tw-flex-col tw-gap-2'>
              <p className='tw-text-2xs tw-font-medium tw-text-neutral-700'>
                Other
              </p>
              <Textarea
                value={otherText}
                onChange={handleOtherTextChange}
                className={cn(
                  'tw-min-h-0 tw-h-auto',
                  showOtherRequired && 'tw-border-destructive-600'
                )}
                rows={1}
                placeholder='Describe the issue'
                data-testid='negative-feedback-modal-other-text'
              />
              {showOtherRequired && (
                <p className='tw-text-2xs tw-font-normal tw-text-destructive-600'>
                  Please describe the issue
                </p>
              )}
            </div>
          )}
          <div className='tw-flex tw-flex-col tw-gap-2'>
            <p className='tw-text-xs tw-font-medium tw-text-neutral-700'>
              How can the response be improved?
            </p>
            <Textarea
              value={improvementText}
              onChange={(e) => {
                setImprovementText(e.target.value)
                if (
                  showValidationError &&
                  shouldClearError(selectedOptions, e.target.value)
                ) {
                  setShowValidationError(false)
                }
              }}
              placeholder='Provide some details or suggest improvements'
              data-testid='negative-feedback-modal-improvement-text'
              aria-describedby='improvement-helper'
              className={cn(
                showValidationError &&
                  improvementText.trim() === '' &&
                  'tw-border-destructive-600'
              )}
            />
            <span
              id='improvement-helper'
              className='tw-block tw-mt-1 tw-text-2xs tw-text-neutral-400'
            >
              We review your feedback to fine-tune our AI and improve the
              quality of future responses.
            </span>
          </div>
        </div>
        <div className='tw-flex tw-flex-col tw-gap-2'>
          {showValidationError && (
            <p
              className='tw-text-2xs tw-font-normal tw-text-destructive-600 tw-text-right'
              role='alert'
            >
              Please select at least one option and provide improvement
              suggestions
            </p>
          )}
          <div className='tw-flex tw-justify-end tw-w-full tw-gap-2'>
            <Button
              variant='gray'
              className='focus-visible:tw-ring-2 focus-visible:tw-ring-primary-600'
              onClick={() => setOpen(false)}
              data-testid='feedback-modal-cancel-btn'
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              className='focus-visible:tw-ring-2 focus-visible:tw-ring-primary-700'
              onClick={handleSubmit}
              data-testid='feedback-modal-submit-btn'
              disabled={isSubmitting}
            >
              Submit
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
